import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyCall from "@/components/StickyCall";
import BookingForm from "@/components/BookingForm";
import Seo, { LOCAL_BUSINESS_SCHEMA } from "@/components/Seo";
import { SERVICES, COMPANY, CITIES } from "@/lib/data";
import * as Icons from "lucide-react";
import { ArrowRight, Phone, CheckCircle2 } from "lucide-react";

const SERVICE_CONTENT = {
  "wheelchair-accessible-transportation": {
    intro: "Wheelchair accessible transportation across Virginia, designed for safety, dignity, and comfort. Our ADA-compliant vehicles are equipped with hydraulic lifts, ramps, and four-point securement systems to safely transport passengers in standard, manual, and power wheelchairs.",
    bullets: [
      "Hydraulic lift–equipped wheelchair vans",
      "Four-point tie-down securement for safety",
      "Trained, certified wheelchair-transport drivers",
      "Spacious vehicles with companion seating",
    ],
  },
  "dialysis-transportation": {
    intro: "Dialysis is a lifeline — and the ride to it should be just as dependable as the treatment itself. Comfort Care provides recurring, on-time dialysis transportation across Virginia, with the consistency and care that dialysis patients deserve.",
    bullets: [
      "Recurring transportation schedules (3x weekly, etc.)",
      "Driver continuity for familiar, comforting care",
      "Wheelchair-accessible vehicles for non-ambulatory patients",
      "Coordination with dialysis center schedules",
    ],
  },
  "hospital-discharge-transportation": {
    intro: "After a hospital stay, the last thing patients and families need is transportation stress. Our hospital discharge service provides safe, comfortable rides home — coordinated with discharge planners, case managers, and family members.",
    bullets: [
      "Same-day and scheduled discharge pickups",
      "Door-through-door assistance",
      "Wheelchair, stretcher, and ambulatory transport",
      "Coordination with hospital case management",
    ],
  },
};

const DEFAULT_CONTENT = (title) => ({
  intro: `${title} from Comfort Care Transportation. We deliver safe, dependable, ADA-compliant non-emergency medical transportation throughout Virginia, with a focus on dignity, comfort, and reliability for every passenger.`,
  bullets: [
    "ADA-compliant vehicles maintained to the highest standard",
    "Trained, compassionate drivers focused on patient safety",
    "Door-through-door service for full comfort",
    "Statewide Virginia coverage with recurring & one-time scheduling",
  ],
});

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    if (service) {
      window.scrollTo(0, 0);
    }
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  const content = SERVICE_CONTENT[slug] || DEFAULT_CONTENT(service.title);
  const Icon = Icons[service.icon] || Icons.Heart;
  const otherServices = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  const seoTitle = `${service.title} | Virginia · DC · Maryland | Comfort Care NEMT 571-689-4470`;
  const seoDescription = `${service.short} Trusted ${service.title.toLowerCase()} across Virginia, Washington DC, and Maryland. ADA-compliant fleet, trained drivers. Call 571-689-4470 to book.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      LOCAL_BUSINESS_SCHEMA,
      {
        "@type": "Service",
        "@id": `https://comfortcarenemt.com/services/${slug}#service`,
        serviceType: service.title,
        name: service.title,
        description: content.intro,
        provider: { "@id": "https://comfortcarenemt.com/#business" },
        areaServed: [
          { "@type": "State", name: "Virginia" },
          { "@type": "City", name: "Washington, D.C." },
          { "@type": "State", name: "Maryland" },
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: service.title,
          itemListElement: content.bullets.map((b) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: b },
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://comfortcarenemt.com/" },
          { "@type": "ListItem", position: 2, name: "Services", item: "https://comfortcarenemt.com/#services" },
          { "@type": "ListItem", position: 3, name: service.title, item: `https://comfortcarenemt.com/services/${slug}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={`/services/${slug}`}
        image="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/r4712rd0_84EBC7D9-7177-4F0A-9C38-BA0A08E3B4F6.PNG"
        keywords={[
          service.title,
          `${service.title} Virginia`,
          `${service.title} DC`,
          `${service.title} Maryland`,
          "NEMT",
          "non emergency medical transportation",
          "wheelchair accessible van",
          "Comfort Care Transportation",
        ]}
        jsonLd={jsonLd}
      />
      <Header />
      <main className="pt-24">
        <section className="grain-bg py-16 md:py-24">
          <div className="container-pad mx-auto max-w-7xl">
            <Link to="/" className="text-sm text-navy/60 hover:text-teal-600">← Back to Home</Link>
            <div className="grid lg:grid-cols-12 gap-10 mt-6 items-center">
              <div className="lg:col-span-7">
                <div className="w-16 h-16 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-600">
                  <Icon className="w-8 h-8" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-navy tracking-tight mt-6 leading-[1.05]">
                  {service.title.split(" ").slice(0, -1).join(" ")} <strong className="font-bold text-teal-600">{service.title.split(" ").slice(-1)[0]}</strong>
                </h1>
                <p className="mt-6 text-lg text-navy/70 leading-relaxed max-w-2xl font-light">{service.short}</p>
                <div className="flex flex-wrap gap-3 mt-7">
                  <a href="#book" className="btn-primary">Request a Ride <ArrowRight className="w-4 h-4" /></a>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="btn-outline">
                    <Phone className="w-4 h-4" /> Call {COMPANY.phone}
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <img
                  src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/p9qldnae_Screenshot%202026-06-05%20at%209.17.14%E2%80%AFAM.png"
                  alt={service.title}
                  className="rounded-3xl w-full h-[380px] object-cover shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="section-pad">
          <div className="container-pad mx-auto max-w-4xl">
            <p className="text-lg text-navy/75 leading-relaxed font-light">{content.intro}</p>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-5">What's included</h3>
            <ul className="space-y-3">
              {content.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                  <span className="text-navy/85">{b}</span>
                </li>
              ))}
            </ul>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-4">Available throughout Virginia</h3>
            <div className="flex flex-wrap gap-2">
              {CITIES.map((c) => (
                <Link key={c.slug} to={`/service-area/${c.slug}`} className="px-4 py-1.5 rounded-full bg-sand border border-sand-200 text-sm text-navy/75 hover:border-teal-300 hover:text-teal-700 transition">
                  {c.name}
                </Link>
              ))}
            </div>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-5">Other services you may need</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {otherServices.map((s) => {
                const I = Icons[s.icon] || Icons.Heart;
                return (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="card-soft p-5 flex items-center gap-4 group">
                    <div className="w-11 h-11 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-600 group-hover:bg-teal-500 group-hover:text-white transition">
                      <I className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <div className="font-heading font-semibold text-navy text-sm">{s.title}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-teal-500" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section id="book" className="section-pad bg-sand">
          <div className="container-pad mx-auto max-w-6xl">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-light text-navy leading-tight">
                Request <strong className="font-bold text-teal-600">{service.title.toLowerCase()}</strong>
              </h2>
              <p className="text-navy/65 mt-3">A scheduling specialist will confirm your trip within hours.</p>
            </div>
            <BookingForm />
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
